<script setup lang="ts">
import iconHeading from "@/assets/images/ranking/icon_heading_flow.svg";
import step01Img from "@/assets/images/step01.png";
import step02Img from "@/assets/images/step02.png";
import step03Img from "@/assets/images/step03.png";

const steps = [
  {
    no: "STEP.01",
    stepImg: step01Img,
    title: "見積もり申し込み",
    body: 'このページの<b>「見積もり依頼スタート！」ボタンをクリック</b>して、見積もりに必要な情報をご入力ください。',
    cls: "flow-step01",
  },
  {
    no: "STEP.02",
    stepImg: step02Img,
    title: "見積もり・比較",
    body: "ご入力いただいた情報をもとに、<b>保険のプロがシミュレーション</b>を行い、あなたに<b>おすすめの火災保険をご提案</b>します。",
    cls: "flow-step02",
  },
  {
    no: "STEP.03",
    stepImg: step03Img,
    title: "加入のご検討",
    body: '見積もりをもとにご検討ください。<b>保険のプロへの無料相談も行っております。</b><br><span class="flow-caution">※強引な勧誘等は一切いたしませんので、ご安心ください。</span>',
    cls: "flow-step03",
  },
];
</script>

<template>
  <section class="flow">
    <AtomsRankingHeading
      :icon="iconHeading"
      title="見積もり依頼サービスご利用の流れ"
    />
    <p class="flow-lead">
      どなたにも安心してご利用いただけるよう、最適な保険選びのサポートをいたします。何度ご相談いただいても無料です！
    </p>

    <div class="flow-steps">
      <ol>
        <li
          v-for="step in steps"
          :key="step.no"
          :class="step.cls"
        >
          <h3>
            <img :src="step.stepImg" :alt="step.no" />
            {{ step.title }}
          </h3>
          <p v-html="step.body" />
        </li>
      </ol>
    </div>

    <div class="flow-annotation">
      <p class="flow-annotation-title">送信後の流れ</p>
      <p class="flow-annotation-text">
        フォーム送信後、試算結果がすぐに表示されるわけではありません。専門の代理店担当者よりお電話またはメールにてご連絡し、詳細をお伺いした上でご提案します。
      </p>
    </div>

    <!-- CTA（ページから既存コンポーネントを差し込む） -->
    <slot name="cta" />

    <div class="flow-surveyWrap">
      <OrganismsRankingSurveyOverview />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.flow-lead {
  padding: 15px;
  font-size: px(13);
  line-height: 1.5;
  color: $colorBasicText;
}

.flow-steps {
  padding: 5% 4%;
  background: url(~/assets/images/bg_pattern_02.gif) no-repeat 0 0;
  background-size: 100% 100%;

  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    padding: 3% 4% 8%;
    background-color: #fff;
    box-sizing: border-box;

    &:not(:last-of-type) {
      margin: 0 0 8%;
      position: relative;

      &::after {
        content: '';
        border: 10px solid transparent;
        border-top-color: #1b59b4;
        border-top-width: 10px;
        border-bottom-width: 0;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translate(-50%, 200%);
      }
    }

    h3 {
      margin: 0 0 3%;
      color: $colorDarkBlue;
      font-weight: 700;

      img {
        margin: 0 0.5em 0 0;
        width: 23.2%;
        display: inline-block;
        vertical-align: middle;
      }
    }

    p {
      padding: 0 37% 0 0;
      min-height: 7em;
      font-size: px(12.9);

      :deep(b) {
        font-weight: 700;
      }

      :deep(.flow-caution) {
        margin: 0 0 0 1em;
        padding: 7px 0 0 0;
        font-size: px(11);
        color: #666;
        display: block;
        text-indent: -1em;
      }
    }

    &.flow-step01 p {
      background: url(~/assets/images/icon_request.png) no-repeat 97% center;
      background-size: 25% auto;
    }
    &.flow-step02 p {
      background: url(~/assets/images/icon_estimation.png) no-repeat 97% center;
      background-size: 25% auto;
    }
    &.flow-step03 p {
      background: url(~/assets/images/icon_consideration.png) no-repeat 97% center;
      background-size: 25% auto;
    }
  }
}

.flow-annotation {
  margin: 16px 15px;
  padding: 12px 12px 16px;
  background-color: #f8f7f3;
}

.flow-annotation-title {
  font-size: px(15);
  font-weight: 700;
  color: $colorBlue;
}

.flow-annotation-text {
  margin-top: 4px;
  font-size: px(13);
  line-height: 1.5;
  color: $colorBasicText;
}

.flow-surveyWrap {
  margin: -20px 0 0;
  padding-bottom: 40px;
}

@include media(pc) {
  .flow-lead {
    padding: 15px 0 40px;
    font-size: px(15);
  }

  .flow-steps {
    padding: 0;
    background: none;

    ol {
      display: flex;
      justify-content: space-between;
    }

    li {
      padding: 21px 15px;
      width: 196px;
      min-height: 370px;
      background: url(~/assets/images/bg_pattern_02.gif) no-repeat 0 0;
      background-size: 100% 100%;

      &:not(:last-of-type) {
        margin: 0 26px 0 0;

        &::after {
          border-top-color: transparent;
          border-left-color: #1b59b4;
          border-left-width: 10px;
          border-right-width: 0;
          border-top-width: 10px;
          border-bottom-width: 10px;
          top: 50%;
          right: 0;
          left: auto;
          bottom: auto;
          transform: translate(19px, -50%);
        }
      }

      h3 {
        margin: 0 0 1.3em;
        font-size: px(18.9);
        text-align: center;

        img {
          margin: 0 auto 5px;
          width: 92px;
          height: 24px;
          display: block;
        }
      }

      p {
        padding: 89px 0 0 0;
        font-size: px(15);
      }

      &.flow-step01 p {
        background: url(~/assets/images/icon_request.png) no-repeat center top;
        background-size: 74px auto;
      }
      &.flow-step02 p {
        background: url(~/assets/images/icon_estimation.png) no-repeat center top;
        background-size: 76px auto;
      }
      &.flow-step03 p {
        background: url(~/assets/images/icon_consideration.png) no-repeat center top;
        background-size: 94px auto;
      }
    }
  }

  .flow-annotation {
    margin: 16px 0;
    padding: 12px 16px;
  }

  .flow-surveyWrap {
    margin: -43px 0 0;
    padding-bottom: 0;
  }
}
</style>
